import React, { useEffect, useRef } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { TrashIcon, DotsVerticalIcon } from '@radix-ui/react-icons';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@producer/ui-kit';

import { StatusSelector } from '../../process/components/status-selector';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { PriorityIcon, PrioritySelector } from '../../process/components/priority-selector';
import TaskStepSelector from './task-step-selector';
import { TaskDateSelector } from './task-date-selector';
import { authStore } from '../../../core/stores/auth-store';
import { useCurrentProject } from '../../../hooks/use-current-project';
import { Left, Wrapper } from './styled-tasks-components';
import { Project, Task, TaskStatus } from '../../../app/entities';
import { TaskProjectSelector } from './task-project-selector';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { AssigneeSelector } from '../../../components/assignee-selector/assignee-selector';
import { TagsViewer } from './tags-viewer';
import { openModal } from '../../../core/modal/open-modal';

import styles from './task-item.module.scss';
import { context } from '../../../app/context';


type TaskItemProps = {
  task: Task;

  /**
   * Override the default navigation when clicking on a task
   * @param taskId
   */
  onSelect?: (taskId: string) => void;
};

export const TaskItem: React.FC<TaskItemProps> = observer(({ task, onSelect }) => {
  const currentMember = context.member;
  const currentProject = useCurrentProject();
  const taskProject = task.projectId ? Project.getOne(task.projectId) : undefined;
  const navigate = useNavigate();

  const dueDateStatus = useRef<'warning' | 'danger'>(null);

  const isViewOnly =
    !currentMember || (currentMember.role === 'guest' && currentMember._id !== task.assigneeId);

  useEffect(() => {
    if (!task.dueDate || [TaskStatus.Done, TaskStatus.Canceled].includes(task.status)) {
      return;
    }

    if (task.dueDate) {
      const today = new Date();
      const dueDate = new Date(task.dueDate);

      const dateDifference = differenceInCalendarDays(dueDate, today);

      if (dateDifference <= 0) {
        dueDateStatus.current = 'danger';
      } else if (dateDifference < 4) {
        dueDateStatus.current = 'warning';
      }
    }
  }, [task.dueDate, task.status]);

  const handleOpenDelete = async () => {
    if (isViewOnly) {
      return authStore.setAuthorized(false);
    }

    openModal(DeleteDialog, {
      title: 'Delete task',
      text: `The task will be deleted completely from the project.`,
      onSubmit: () => {
        task.delete();
      },
    });
  };

  const menuItems: React.ComponentProps<typeof DropdownMenu>['items'] = [
    {
      title: 'Delete task',
      icon: <TrashIcon />,
      type: 'danger' as const,
      onSelect: handleOpenDelete,
    },
  ];

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onSelect ? onSelect(task._id) : navigate(`./${task._id}`);
  };

  return (
    <div className={styles.strip} onClick={handleClick}>
      <Wrapper data-full-compact="true">
        <Left>
          <StatusSelector
            isViewOnly={isViewOnly}
            disableTooltip={isViewOnly}
            status={task.status}
            onChange={(status) => task.update({ status })}
            statusMap={Task.StatusMapper}
          />

          <PrioritySelector
            isViewOnly={isViewOnly}
            priority={task.priority}
            onChange={(priority) => task.update({ priority })}
            trigger={
              <div className={styles.priority}>
                <PriorityIcon priority={task.priority} data-disabled={isViewOnly} />
              </div>
            }
          />

          <AssigneeSelector
            isViewOnly={isViewOnly}
            project={taskProject}
            selectedMemberId={task.assigneeId}
            onChange={(assigneeId) => {
              task.update({
                assigneeId: assigneeId || null,
                projectId: task.projectId,
              });
            }}
            iconOnly
          />
        </Left>

        <p className={styles.title}>{task.title}</p>
      </Wrapper>

      <Wrapper>
        <TagsViewer targetId={task._id} targetType="task" overflow="collapse" alignment="end" />

        {!currentProject && (
          <TaskProjectSelector
            onChange={(projectId) => task.update({ projectId, stepId: null })}
            selectedProjectId={task.projectId}
            isViewOnly={isViewOnly}
            triggerVariant={'minimal'}
          />
        )}

        <Tooltip text="Select project first" disabled={isViewOnly || !!task.projectId}>
          <div>
            <TaskStepSelector
              triggerVariant="minimal"
              isViewOnly={isViewOnly || !task.projectId}
              project={taskProject}
              onChange={(stepId) => task.update({ stepId, projectId: task.projectId })}
              selectedStepId={task.stepId || undefined}
            />
          </div>
        </Tooltip>

        <TaskDateSelector
          isViewOnly={isViewOnly}
          date={task.dueDate}
          onUpdate={(date) => task.update({ dueDate: date })}
          status={task.status}
        />

        <DropdownMenu items={menuItems} trigger={<IconButton icon={<DotsVerticalIcon />} />} />
      </Wrapper>
    </div>
  );
});
